import axios from 'axios';

import { COREAPI, getUserData, QUERYAPI } from '../constants/global';
import type {
	CitizensSitrepOutcome,
	CoreReportsOutcome,
	ICitizensSitrepContext,
	ICitizensSitrepItem,
	ICoreReportItem,
} from '../interface/child-agent';

// Two situation-report sources, one isolated client per base URL. Both attach
// the agent's core JWT + PartyId and omit the 401->logout interceptor (a sitrep
// outage must never sign the agent out).
const attachAuth = (client: ReturnType<typeof axios.create>) => {
	client.interceptors.request.use((config) => {
		const { token, user } = getUserData();
		if (token) config.headers.Authorization = `Bearer ${token}`;
		if (user?.PartyId) config.headers.PartyId = user.PartyId;
		return config;
	});
	client.interceptors.response.use((response) => {
		if (typeof response.data === 'string' && response.data) {
			try {
				response.data = JSON.parse(response.data);
			} catch {
				// Not JSON — leave as-is.
			}
		}
		return response;
	});
	return client;
};

const coreApi = attachAuth(axios.create({ baseURL: COREAPI }));
const queryApi = attachAuth(axios.create({ baseURL: QUERYAPI }));

const REPORTS_PAGE_SIZE = 100;

/**
 * Source (a): Core incident reports. There is NO server-side filter by agent or
 * entity, so we page the recent reports and the caller filters by userId. The
 * flattened paged envelope puts the rows under `data`.
 */
export const getCoreReportsForAgent = async (): Promise<CoreReportsOutcome> => {
	try {
		const res = await coreApi.get<{ data?: ICoreReportItem[] }>('/api/v1/Report', {
			params: { PageNumber: 1, PageSize: REPORTS_PAGE_SIZE },
			validateStatus: () => true,
		});
		const items = Array.isArray(res.data?.data) ? res.data?.data : [];
		return { reachable: true, httpStatus: res.status, items };
	} catch {
		return { reachable: false };
	}
};

/** Keep only the reports filed by this agent (match on the userId on each row). */
export const filterReportsByUser = (items: ICoreReportItem[], userId?: string): ICoreReportItem[] => {
	if (!userId) return items;
	return items.filter((r) => String(r.userId ?? '') === String(userId));
};

/**
 * Guard for source (b): the citizens situation-report endpoint requires the
 * child's full hierarchy (`wards`, `localGovernments`, `geoZones`, `zones` are
 * mandatory server-side). The drawer only receives the PU code today, so this
 * returns false and the tab degrades to source (a). When the overview widget
 * starts passing resolved hierarchy codes, this flips on with no other change.
 */
export const canQueryCitizensSitrep = (ctx?: Partial<ICitizensSitrepContext> | null): ctx is ICitizensSitrepContext =>
	!!ctx &&
	!!ctx.poolingUnitCodes?.length &&
	!!ctx.wards?.length &&
	!!ctx.localGovernments?.length &&
	!!ctx.geoZones?.length &&
	!!ctx.zones?.length;

/**
 * Source (b): citizens-agent situation reports for the child entity. POST with a
 * multi-field JSON body (native arrays — no repeatable-key trick needed). Only
 * call when `canQueryCitizensSitrep` passes; the required arrays 400 otherwise.
 */
export const getCitizensSituationReports = async (ctx: ICitizensSitrepContext): Promise<CitizensSitrepOutcome> => {
	const body = {
		pageNumber: 1,
		pageSize: 50,
		poolingUnitCodes: ctx.poolingUnitCodes,
		wards: ctx.wards,
		localGovernments: ctx.localGovernments,
		geoZones: ctx.geoZones,
		zones: ctx.zones,
		...(ctx.electionId ? { elections: [String(ctx.electionId)] } : {}),
	};
	try {
		const res = await queryApi.post<{ data?: ICitizensSitrepItem[] }>(
			'/api/v1/citizens-agent/Result/get-situation-report',
			body,
			{ validateStatus: () => true }
		);
		const items = Array.isArray(res.data?.data) ? res.data?.data : [];
		return { reachable: true, httpStatus: res.status, items };
	} catch {
		return { reachable: false };
	}
};
